import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { Button } from "@/components/Button";
import { LoadError } from "@/components/LoadError";
import { SettingsSection, SettingsSkeleton } from "./SettingsSection";
import { Toast, useToast } from "@/components/Toast";
import { formatDateTime } from "@/lib/formatDateTime";
import { useApi } from "@/lib/api";

interface PendingIdentity {
  resourceId: string;
  resourceName: string;
  kind: string;
  fingerprint: string;
  firstSeenAt: string;
}

interface PolicySummary {
  policy: string;
  resourcesWithoutIdentity: number;
  resourcesAwaitingApproval: number;
  resourcesTrustedUnverified: number;
}

const POLICIES = [
  {
    value: "TrustOnFirstUse",
    label: "Trust on first use (default)",
    description:
      "Tendra records the identity a host presents on the first connection and checks it on every connection after that. If the identity changes, Tendra refuses the connection. No manual verification is required.",
  },
  {
    value: "RequireApproval",
    label: "Require approval",
    description:
      "Tendra records the identity a host presents, but connections are blocked until an administrator approves it. Choose this when each host must be manually verified before it can be used.",
  },
  {
    value: "RequireProvisioned",
    label: "Require a provisioned identity",
    description:
      "Tendra only trusts fingerprints supplied by an administrator, allowing a host to be verified before the first connection. Choose this when host identities must be known in advance and fingerprints are available from your hosts, inventory, or build pipeline.",
  },
] as const;

export function HostIdentityPolicySettings() {
  const api = useApi();
  const { orgId } = useParams();

  const [summary, setSummary] = useState<PolicySummary | null>(null);
  const [selected, setSelected] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingIdentity[]>([]);
  const [failed, setFailed] = useState(false);
  const [approving, setApproving] = useState<string | null>(null);
  const { toast, showToast } = useToast();

  const path = `/organizations/${orgId}/host-identity-policy`;

  const load = useCallback(async () => {
    const result = await api.get<PolicySummary>(path);

    if (result) {
      setSummary(result);
      setSelected(result.policy);
      setFailed(false);
    } else {
      setFailed(true);

      return;
    }

    setPending(
      (await api.get<PendingIdentity[]>(`${path}/awaiting-approval`)) ?? [],
    );
  }, [api, path]);

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    void load();
  }, [load]);

  async function save() {
    setSaving(true);
    setError(null);

    try {
      await api.send(path, "PUT", { policy: selected });
      await load();
      showToast("Host identity policy updated.", "success");
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "Couldn't save.");
    } finally {
      setSaving(false);
    }
  }

  async function approve(identity: PendingIdentity) {
    setApproving(identity.resourceId);

    try {
      await api.send(
        `/organizations/${orgId}/resources/${identity.resourceId}/host-identity/approve`,
        "POST",
      );
      await load();
      showToast(
        `Host identity approved for ${identity.resourceName}.`,
        "success",
      );
    } catch (cause: unknown) {
      showToast(
        cause instanceof Error
          ? cause.message
          : "Couldn't approve the identity.",
        "error",
      );
    } finally {
      setApproving(null);
    }
  }

  const tightening = selected !== "TrustOnFirstUse";

  return (
    <SettingsSection
      className="mb-8"
      title="Host identity"
      description="RDP and SSH don't prove which host answered a connection. Tendra protects against this by recording each host's identity and refusing future connections if it changes. This protection is enabled by default. This setting controls how a host's identity is trusted the first time, with stricter options requiring manual verification."
    >
      {failed ? (
        <LoadError what="host identity" onRetry={() => void load()} />
      ) : summary === null ? (
        <SettingsSkeleton />
      ) : (
        <div className="space-y-3">
          <fieldset className="space-y-2">
            <legend className="sr-only">Host identity policy</legend>
            {POLICIES.map((policy) => (
              <label
                key={policy.value}
                className={`flex cursor-pointer gap-3 rounded-lg border p-3 ${
                  selected === policy.value
                    ? "border-accent bg-surface/40"
                    : "border-muted bg-surface/40 hover:border-accent/50"
                }`}
              >
                <input
                  type="radio"
                  name="hostIdentityPolicy"
                  value={policy.value}
                  checked={selected === policy.value}
                  onChange={() => setSelected(policy.value)}
                  className="mt-1 h-4 w-4 shrink-0 accent-accent"
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium">
                    {policy.label}
                  </span>
                  <span className="mt-0.5 block text-xs text-foreground/70">
                    {policy.description}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>

          {tightening && summary.resourcesWithoutIdentity > 0 && (
            <p className="rounded-lg border border-muted bg-surface p-3 text-sm">
              {summary.resourcesWithoutIdentity}{" "}
              {summary.resourcesWithoutIdentity === 1
                ? "resource has"
                : "resources have"}{" "}
              no host identity recorded yet.
              {selected === "RequireProvisioned"
                ? " Connections will be refused until a fingerprint is provided for each one."
                : " Their identities will be recorded on the next connection and will need to be approved."}
            </p>
          )}

          {selected === "RequireApproval" &&
            selected !== summary.policy &&
            summary.resourcesTrustedUnverified > 0 && (
              <p className="rounded-lg border border-muted bg-surface p-3 text-sm">
                {summary.resourcesTrustedUnverified}{" "}
                {summary.resourcesTrustedUnverified === 1
                  ? "resource has an identity recorded automatically on first contact"
                  : "resources have identities recorded automatically on first contact"}
                . Saving this puts{" "}
                {summary.resourcesTrustedUnverified === 1 ? "it" : "them"} in
                the queue below for approval, and sessions will be refused until
                then.
              </p>
            )}

          {selected === "RequireApproval" &&
            selected === summary.policy &&
            pending.length === 0 && (
              <p className="rounded-lg border border-muted bg-surface p-3 text-sm">
                Nothing is awaiting approval. New host identities appear here
                shortly after resources are added, and connections are refused
                until you approve them.
              </p>
            )}

          {pending.length > 0 && (
            <div>
              <p className="mb-2 text-sm">
                {pending.length === 1
                  ? "One host identity is"
                  : `${pending.length} host identities are`}{" "}
                awaiting approval. Sessions to{" "}
                {pending.length === 1 ? "it" : "them"} are refused until
                approved.
              </p>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[40rem] text-left text-sm">
                  <thead>
                    <tr>
                      {["Resource", "Fingerprint", "First seen"].map(
                        (column) => (
                          <th
                            key={column}
                            scope="col"
                            className="border-b border-surface pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wider text-foreground/70"
                          >
                            {column}
                          </th>
                        ),
                      )}
                      <th
                        scope="col"
                        className="border-b border-surface pb-2.5"
                      >
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pending.map((identity) => (
                      <tr
                        key={identity.resourceId}
                        className="h-14 border-b border-surface/70 hover:bg-surface"
                      >
                        <td className="py-2 pr-4">{identity.resourceName}</td>
                        <td className="py-2 pr-4">
                          <span className="break-all font-mono text-xs text-foreground/70">
                            {identity.fingerprint}
                          </span>
                        </td>
                        <td className="whitespace-nowrap py-2 pr-4 tabular-nums text-foreground/70">
                          {formatDateTime(identity.firstSeenAt)}
                        </td>
                        <td className="py-2 text-right">
                          <Button
                            variant="primary"
                            size="sm"
                            disabled={approving !== null}
                            onClick={() => approve(identity)}
                          >
                            {approving === identity.resourceId
                              ? "Approving..."
                              : "Approve"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {error && <p className="text-sm text-danger">{error}</p>}

          <div className="flex items-center gap-3">
            <Button
              variant="primary"
              disabled={saving || selected === summary.policy}
              onClick={save}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </SettingsSection>
  );
}
